<template>
  <v-dialog :model-value="modelValue" max-width="920" persistent @update:model-value="emit('update:modelValue', $event)">
    <div class="wage-excel-migration-dialog">
      <div class="wage-excel-migration-dialog__header">
        <div class="header-icon">
          <v-icon size="18" color="white">mdi-file-excel</v-icon>
        </div>
        <h2 class="header-title">임금 엑셀 마이그레이션</h2>
        <span class="header-badge">{{ wageTypeLabel }}</span>
        <button
          type="button"
          class="wage-excel-migration-dialog__close"
          :disabled="loading"
          aria-label="닫기"
          @click="close"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="wage-excel-migration-dialog__body">
        <LoadingOverlay :show="loading" :text="loadingText" variant="default" />

        <p class="migration-intro">
          타 프로그램에서 사용하던 임금 데이터를 엑셀로 가져옵니다.
          업로드 시 <strong>4대보험·공제 고정 체크가 모두 ON</strong> 상태로 저장되어 자동계산 없이 그대로 박제할 수 있습니다.
        </p>

        <div class="migration-actions">
          <v-btn variant="flat" class="btn-refresh" size="small" :disabled="loading" @click="downloadTemplate">
            <v-icon size="14">mdi-download</v-icon>
            양식 다운로드
          </v-btn>
          <label class="migration-upload-label">
            <input
              ref="fileInputRef"
              type="file"
              accept=".xlsx,.xls"
              class="migration-upload-input"
              :disabled="loading"
              @change="onFileSelected"
            />
            <v-btn variant="flat" class="btn-search" size="small" tag="span" :disabled="loading">
              <v-icon size="14">mdi-upload</v-icon>
              엑셀 선택
            </v-btn>
          </label>
          <span v-if="selectedFileName" class="migration-file-name">{{ selectedFileName }}</span>
        </div>

        <div v-if="summary" class="migration-summary">
          <span>총 {{ summary.total }}건</span>
          <span class="ok">정상 {{ summary.valid }}건</span>
          <span v-if="summary.missingEmployees" class="warn">미등록 사원 {{ summary.missingEmployees }}건</span>
          <span v-if="summary.errors" class="err">오류 {{ summary.errors }}건</span>
        </div>

        <label v-if="summary?.missingEmployees" class="migration-register-option">
          <input v-model="registerMissingEmployees" type="checkbox" />
          <span>미등록 사원 자동 등록 (생년월일 필수)</span>
        </label>

        <div v-if="previewRows.length" class="migration-preview-wrap">
          <table class="migration-preview-table">
            <thead>
              <tr>
                <th>행</th>
                <th>성명</th>
                <th>생년월일</th>
                <th>직종</th>
                <th>과세합계</th>
                <th>상태</th>
                <th>메시지</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in previewRows"
                :key="row.rowNum"
                :class="{
                  'row-error': row.status === 'error',
                  'row-warn': row.status === 'missing_employee',
                }"
              >
                <td>{{ row.rowNum }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.birth || '-' }}</td>
                <td>{{ row.job || '-' }}</td>
                <td class="num">{{ formatAmount(row) }}</td>
                <td>
                  <span class="status-chip" :class="`status-chip--${row.status}`">{{ statusLabel(row.status) }}</span>
                </td>
                <td class="msg-cell">
                  <template v-if="row.errors?.length">
                    <div v-for="(msg, i) in row.errors" :key="i">{{ msg }}</div>
                  </template>
                  <template v-else-if="row.warnings?.length">
                    <div v-for="(msg, i) in row.warnings" :key="i">{{ msg }}</div>
                  </template>
                  <template v-else>—</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="wage-excel-migration-dialog__footer">
        <v-btn variant="text" class="btn-close" :disabled="loading" @click="close">닫기</v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          class="btn-save"
          :disabled="loading || !canImport"
          @click="runImport"
        >
          <v-icon v-if="importing" size="14" class="mdi-spin">mdi-loading</v-icon>
          <template v-else>{{ importButtonLabel }}</template>
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { commonWageApi, homeScheduleApi } from '@/services/api';

export type WageMigrationType = 'home' | 'facility';

interface MigrationPreviewRow {
  rowNum: number;
  name: string;
  birth?: string | null;
  job?: string;
  status: 'ok' | 'error' | 'missing_employee';
  errors?: string[];
  warnings?: string[];
  preview?: Record<string, unknown>;
}

interface MigrationSummary {
  total: number;
  valid: number;
  missingEmployees: number;
  errors: number;
}

const props = defineProps<{
  modelValue: boolean;
  wageType: WageMigrationType;
  mCode: string;
  yearMonth: string;
  bizGubun?: string;
  payDate?: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  imported: [];
}>();

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFileName = ref('');
const loading = ref(false);
const importing = ref(false);
const loadingText = ref('검증 중...');
const previewRows = ref<MigrationPreviewRow[]>([]);
const summary = ref<MigrationSummary | null>(null);
const registerMissingEmployees = ref(true);

const wageTypeLabel = computed(() => (props.wageType === 'home' ? '방문요양' : '요양시설'));

const api = computed(() => (props.wageType === 'home' ? homeScheduleApi : commonWageApi));

const importableRows = computed(() =>
  previewRows.value.filter((r) => {
    if (r.status === 'ok') return true;
    if (r.status === 'missing_employee' && registerMissingEmployees.value) return true;
    return false;
  })
);

const canImport = computed(() => importableRows.value.length > 0);

const importButtonLabel = computed(() => {
  const n = importableRows.value.length;
  if (!n) return '업로드';
  return `${n}건 업로드`;
});

const resetState = () => {
  selectedFileName.value = '';
  previewRows.value = [];
  summary.value = null;
  registerMissingEmployees.value = true;
  if (fileInputRef.value) fileInputRef.value.value = '';
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) resetState();
  }
);

const close = () => {
  emit('update:modelValue', false);
};

const downloadTemplate = async () => {
  try {
    loading.value = true;
    loadingText.value = '양식 다운로드 중...';
    const response = await api.value.downloadMigrationTemplate();
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `임금마이그레이션_${props.wageType === 'home' ? '방문요양' : '요양시설'}_양식.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`양식 다운로드 실패: ${message}`);
  } finally {
    loading.value = false;
  }
};

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  selectedFileName.value = file.name;
  previewRows.value = [];
  summary.value = null;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('mCode', props.mCode);
  formData.append('yearMonth', props.yearMonth);
  formData.append('bizGubun', props.bizGubun || '');

  try {
    loading.value = true;
    loadingText.value = '엑셀 검증 중...';
    const response = await api.value.validateMigrationExcel(formData);
    if (!response.data?.success) {
      alert(`검증 실패: ${response.data?.message || '알 수 없는 오류'}`);
      return;
    }
    previewRows.value = response.data.data?.rows || [];
    summary.value = response.data.data?.summary || null;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`엑셀 검증 실패: ${message}${error.response?.status ? ` (${error.response.status})` : ''}`);
  } finally {
    loading.value = false;
  }
};

const runImport = async () => {
  if (!canImport.value) return;

  const okCount = importableRows.value.length;
  if (!confirm(`${okCount}건의 임금 데이터를 업로드합니다. 계속하시겠습니까?`)) return;

  try {
    importing.value = true;
    loading.value = true;
    loadingText.value = '업로드 중...';

    const payload: Record<string, unknown> = {
      mCode: props.mCode,
      yearMonth: props.yearMonth,
      bizGubun: props.bizGubun || '',
      rows: importableRows.value,
      registerMissingEmployees: registerMissingEmployees.value,
    };
    if (props.wageType === 'home' && props.payDate) {
      payload.payDate = props.payDate;
    }

    const response = await api.value.importMigration(payload as any);
    if (!response.data?.success) {
      alert(`업로드 실패: ${response.data?.message || '알 수 없는 오류'}`);
      return;
    }

    const { savedCount = 0, registeredCount = 0, results = [] } = response.data.data || {};
    const failed = (results as any[]).filter((r) => !r.success);
    let msg = `${savedCount}건 저장 완료`;
    if (registeredCount) msg += ` (${registeredCount}명 사원 등록)`;
    if (failed.length) {
      msg += `\n\n실패 ${failed.length}건:\n${failed.map((f) => `${f.rowNum}행 ${f.name}: ${f.message}`).join('\n')}`;
    }
    alert(msg);
    emit('imported');
    close();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`업로드 실패: ${message}${error.response?.status ? ` (${error.response.status})` : ''}`);
  } finally {
    importing.value = false;
    loading.value = false;
  }
};

const statusLabel = (status: string) => {
  if (status === 'ok') return '정상';
  if (status === 'missing_employee') return '미등록';
  return '오류';
};

const formatAmount = (row: MigrationPreviewRow) => {
  const p = row.preview || {};
  const val =
    props.wageType === 'home'
      ? Number(p.wageTotal) || 0
      : Number(p.cwhd_wage_total) || 0;
  if (!val) return '-';
  return val.toLocaleString();
};
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/components/wage-excel-migration-modal';
</style>
